import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormArray } from '@angular/forms';
import { IDataCaptureSegment } from '../../../models/data-capture.models';
import {
  AddRespondentRowFormGroup,
  BulkUploadStep,
  UnassignedFile,
} from '../models/add-respondent.types';
import { AddRespondentRowFormService } from '../services/add-respondent-row-form.service';

@Component({
  selector: 'app-add-respondent-bulk-workflow',
  templateUrl: './bulk-upload-workflow.component.html',
  styleUrls: ['./bulk-upload-workflow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block min-w-0',
  },
  standalone: false,
})
export class AddRespondentBulkUploadWorkflowComponent {
  @Input() respondentsArray = new FormArray<AddRespondentRowFormGroup>([]);
  @Input() bulkUploadStep: BulkUploadStep = 'upload';
  @Input() bulkDragActive = false;
  @Input() bulkStaging = false;
  @Input() unassignedFiles: UnassignedFile[] = [];
  @Input() segments: IDataCaptureSegment[] = [];
  @Input() submitted = false;
  @Input() rowUploadErrorLookup: (
    row: AddRespondentRowFormGroup
  ) => string | null = () => null;
  @Input() clearRowUploadError: (row: AddRespondentRowFormGroup) => void =
    () => {};

  @Output() uploadStepSelected = new EventEmitter<void>();
  @Output() reviewStepSelected = new EventEmitter<void>();
  @Output() bulkFilesChange = new EventEmitter<HTMLInputElement>();
  @Output() bulkDragOver = new EventEmitter<DragEvent>();
  @Output() bulkDragLeave = new EventEmitter<DragEvent>();
  @Output() bulkDrop = new EventEmitter<DragEvent>();
  @Output() clearUnassigned = new EventEmitter<void>();
  @Output() addExpertWithoutFiles = new EventEmitter<void>();

  get isBulkUploadStep() {
    return this.bulkUploadStep === 'upload';
  }

  get isBulkReviewStep() {
    return this.bulkUploadStep === 'review';
  }

  get rows() {
    return this.respondentsArray.controls;
  }

  constructor(
    private addRespondentRowFormService: AddRespondentRowFormService,
    private cdr: ChangeDetectorRef
  ) {}

  trackByRow(_: number, row: AddRespondentRowFormGroup) {
    return row;
  }

  getFileStatusIcon(row: AddRespondentRowFormGroup): string {
    return (
      {
        matched: 'check_circle',
        needsReview: 'warning',
        transcriptOnly: 'description',
      }[row.controls.matchStatus.value] || 'check'
    );
  }

  getFileStatusClass(row: AddRespondentRowFormGroup): string {
    return (
      {
        matched: 'bulk-cell-ok',
        needsReview: 'bulk-cell-review',
      }[row.controls.matchStatus.value] || 'bulk-cell-ok'
    );
  }

  hasRowUploadError(row: AddRespondentRowFormGroup) {
    return !!this.rowUploadErrorLookup(row);
  }

  emitBulkFilesChange(target: HTMLInputElement) {
    this.bulkFilesChange.emit(target);
  }

  addRow() {
    this.respondentsArray.push(this.addRespondentRowFormService.create());
    this.cdr.markForCheck();
  }

  removeRow(index: number) {
    this.respondentsArray.removeAt(index);
    this.cdr.markForCheck();
  }

  refresh() {
    this.cdr.markForCheck();
  }

  onFileChange(
    target: HTMLInputElement,
    row: AddRespondentRowFormGroup,
    type: 'audio' | 'transcript'
  ) {
    this.clearRowUploadError(row);

    const [file] = Array.from(target?.files || []);

    if (file) {
      type === 'audio'
        ? this.addRespondentRowFormService.applyAudioFile(row, file)
        : this.addRespondentRowFormService.applyTranscriptFile(row, file);
    }

    target.value = '';
  }

  removeFile(row: AddRespondentRowFormGroup, type: 'audio' | 'transcript') {
    this.clearRowUploadError(row);

    type === 'audio'
      ? this.addRespondentRowFormService.removeAudioFile(row)
      : this.addRespondentRowFormService.removeTranscriptFile(row);

    this.cdr.markForCheck();
  }

  getTableAudioErrorMessage(row: AddRespondentRowFormGroup) {
    if (!this.submitted) {
      return null;
    }

    const control = row.controls.m4aFile;
    const transcriptControl = row.controls.transcriptFile;

    if (control?.hasError('unknownFormat')) {
      return 'Unsupported audio';
    }

    if (control?.hasError('invalidContent')) {
      return 'Invalid audio';
    }

    if (control?.hasError('readError')) {
      return 'Audio read failed';
    }

    if (
      row.hasError('noFile') &&
      !control?.value &&
      !transcriptControl.value &&
      !transcriptControl.errors
    ) {
      return 'Recording or transcript required';
    }

    return null;
  }

  getTableTranscriptErrorMessage(row: AddRespondentRowFormGroup) {
    if (!this.submitted) {
      return null;
    }

    const control = row.controls.transcriptFile;

    if (
      control?.hasError('unknownFormat') ||
      control?.hasError('invalidContent') ||
      control?.hasError('invalidFormat')
    ) {
      return 'Unsupported transcript';
    }

    if (control?.hasError('readError')) {
      return 'Transcript read failed';
    }

    if (row.hasError('durationMismatch')) {
      return 'Duration mismatch';
    }

    if (this.rowUploadErrorLookup(row)) {
      return 'Upload failed';
    }

    return null;
  }

  isSegmentUnoccupied(row: AddRespondentRowFormGroup): boolean {
    const segmentId = row.controls.segment.value;
    if (!segmentId) {
      return false;
    }
    const segment = this.segments.find((s) => s.segmentId === segmentId);
    return !!segment && !segment.occupied;
  }

  getFileTooltip(
    row: AddRespondentRowFormGroup,
    type: 'audio' | 'transcript'
  ): string {
    const file =
      type === 'audio'
        ? row.controls.m4aFile.value
        : row.controls.transcriptFile.value;

    const name = file?.name || '';
    const message = row.controls.matchMessage.value || '';
    const needsReview = row.controls.matchStatus.value === 'needsReview';
    const parts = [name, message].filter(Boolean).join(' - ');

    return needsReview ? parts + ' (click to replace)' : parts;
  }

  getFileExtension(fileName: string): string {
    const dot = fileName.lastIndexOf('.');
    return dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : '';
  }
}
